// src/components/math/MathField.jsx
import React, { useRef, useEffect, useState } from 'react';
import { MathfieldElement } from 'mathlive';

export const MathField = ({ 
  value = '', 
  onChange, 
  placeholder = 'Enter math expression...',
  className = '',
  required = false,
  virtualKeyboards = ['geometric', 'algebra', 'trigonometry', '[default]']
}) => {
  const mathFieldRef = useRef(null);
  const [mathValue, setMathValue] = useState(value);

  // Ensure MathfieldElement is registered as a custom element
  useEffect(() => {
    if (!customElements.get('math-field')) {
      customElements.define('math-field', MathfieldElement);
    }
  }, []);

  useEffect(() => {
    // Initialize MathLive on the element
    if (mathFieldRef.current) {
      // Set initial value if provided
      if (value) {
        mathFieldRef.current.value = value;
        setMathValue(value);
      }

      // Set up event listeners
      const handleMathInput = (event) => {
        const latex = event.target.value;
        setMathValue(latex);
        if (onChange) {
          onChange(latex);
        }
      };

      mathFieldRef.current.addEventListener('input', handleMathInput);

      // Cleanup on unmount
      return () => {
        if (mathFieldRef.current) {
          mathFieldRef.current.removeEventListener('input', handleMathInput);
        }
      };
    }
  }, [value, onChange]);

  // Update value when prop changes
  useEffect(() => {
    if (mathFieldRef.current && value !== mathValue) {
      mathFieldRef.current.value = value;
      setMathValue(value);
    }
  }, [value]);

  // Ensure virtualKeyboards is always an array and convert to string
  const getVirtualKeyboardsString = () => {
    if (!virtualKeyboards) {
      return 'geometric,algebra,trigonometry,[default]';
    }
    
    if (Array.isArray(virtualKeyboards)) {
      return virtualKeyboards.join(',');
    }
    
    if (typeof virtualKeyboards === 'string') {
      return virtualKeyboards;
    }
    
    // Fallback to default
    return 'geometric,algebra,trigonometry,[default]';
  };

  return (
    <math-field
      ref={mathFieldRef}
      className={`math-field-input ${className}`}
      placeholder={placeholder}
      required={required}
      virtual-keyboard-mode="onfocus"
      virtual-keyboards={getVirtualKeyboardsString()}
      smart-fence="true"
      smart-superscript="true"
      style={{
        display: 'block',
        width: '100%',
        minHeight: '3rem',
        padding: '0.75rem',
        border: '1px solid #d1d5db',
        borderRadius: '0.5rem',
        fontSize: '1.1rem',
        backgroundColor: '#fff'
      }}
    ></math-field>
  );
};